import { apiClient, toServiceError } from '@/services/http/client'
import { toCamelDeep, toSnakeDeep } from '@/services/http/caseMap'
import type { CustomerService } from './customers.service'
import type {
  IndividualCustomer,
  CreateCustomerInput,
  UpdateCustomerInput,
  CustomerAssignmentInput,
  CustomerAssignmentEvent,
} from '@/types/customer'
import {
  mapCustomerFromApi,
  mapCustomerListFromApi,
  mapCustomerCreateToApi,
  mapCustomerUpdateToApi,
} from './mappers'

export function createHttpCustomerService(): CustomerService {
  return {
    async list(): Promise<IndividualCustomer[]> {
      try {
        const customers: IndividualCustomer[] = []
        let page = 1
        while (true) {
          const res = await apiClient.get('/customers', { params: { page, page_size: 100 } })
          const items = mapCustomerListFromApi(res.data)
          customers.push(...items)
          if (
            Array.isArray(res.data) ||
            items.length === 0 ||
            customers.length >= (res.data.total ?? customers.length)
          )
            break
          page += 1
        }
        return customers
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async create(input: CreateCustomerInput): Promise<IndividualCustomer> {
      try {
        const res = await apiClient.post('/customers', mapCustomerCreateToApi(input))
        return mapCustomerFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async update(id: string, updates: UpdateCustomerInput): Promise<Partial<IndividualCustomer>> {
      try {
        const res = await apiClient.patch(`/customers/${id}`, mapCustomerUpdateToApi(updates))
        return mapCustomerFromApi(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async remove(id: string, reason = 'User requested deletion'): Promise<void> {
      try {
        await apiClient.delete(`/customers/${id}`, { headers: { 'X-Deletion-Reason': reason } })
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async bulkArchive(ids: string[]): Promise<void> {
      try {
        await apiClient.post('/customers/bulk/archive', { ids })
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async bulkDelete(ids: string[]): Promise<void> {
      try {
        await apiClient.post('/customers/bulk/delete', { ids })
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async bulkAssignCA(ids: string[], caId: string): Promise<{ caId: string; caName: string }> {
      try {
        const res = await apiClient.post('/customers/bulk/assign-ca', {
          ids,
          ca_id: caId,
        })
        const caName: string = res.data?.ca_name ?? res.data?.caName ?? ''
        return { caId, caName }
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async assignCustomer(
      customerId: string,
      input: CustomerAssignmentInput,
      customerType: 'individual' | 'business' = 'individual'
    ): Promise<{
      customerType: string
      customerId: number | string
      assignment: Record<string, any>
    }> {
      try {
        const payload = toSnakeDeep(input)
        const res = await apiClient.put(
          `/customers/governance/${customerType}/${customerId}/assignment`,
          payload
        )
        return toCamelDeep(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async getAssignmentHistory(
      customerId: string,
      customerType: 'individual' | 'business' = 'individual'
    ): Promise<CustomerAssignmentEvent[]> {
      try {
        const res = await apiClient.get(
          `/customers/governance/${customerType}/${customerId}/assignment-history`
        )
        return toCamelDeep<CustomerAssignmentEvent[]>(res.data)
      } catch (err) {
        throw toServiceError(err)
      }
    },

    async resolveClientCustomerId(
      customerId: string,
      customerType: 'individual' | 'business' = 'individual'
    ): Promise<number> {
      try {
        const res = await apiClient.get(
          `/customers/governance/${customerType}/${customerId}/client-owner-alias`
        )
        const ownerId = res.data?.client_customer_id
        if (!Number.isSafeInteger(ownerId) || ownerId < 1 || ownerId > 2_147_483_647) {
          throw new Error('Customer did not return a verified owner alias')
        }
        if (
          res.data?.customer_type !== customerType ||
          String(res.data?.source_customer_id) !== String(customerId)
        ) {
          throw new Error('Customer owner alias does not match the selected customer')
        }
        return ownerId
      } catch (err) {
        throw toServiceError(err)
      }
    },
  }
}
